import { FC, ReactNode, useEffect, useRef } from 'react';

import {
  ContentLayoutVariant,
  HasCommonContent,
  SearchData,
} from '@silvertours/common-landingpages-view';
import {
  ISLAND_STAGE_SEARCH_DATA,
  Runtime,
  analytics,
  useSiteInfo,
} from '@silvertours/front-entities';
import {
  AppInstallBanner,
  ErrorPage,
  MarketingSlider,
  PageLegacy,
  PageNavigationLegacy,
  SearchFormLegacy,
  Suppliers,
  useShowAppBanner,
} from '@silvertours/front-features';
import { ContentModulesRenderer } from './content';
import { ContentModules } from './ContentModules';
import { resolveVariantConfig } from './Body.variant';

type BodyProps = HasCommonContent & {
  variant: ContentLayoutVariant;
  bodyTop?: ReactNode;
  searchData?: SearchData;
  title?: string;
  content: ContentModulesRenderer;
  introductionContent?: ReactNode;
  author?: ReactNode;
  statusCode?: number;
  /** Shows the suppliers section below the stage. The default is `true`. */
  showSuppliers?: boolean;
  /**
   * Shows the marketing slider, if the layout variant has it. The default is
   * `true`.
   */
  showMarketingSlider?: boolean;
};

const Body: FC<BodyProps> = ({
  variant,
  title,
  breadcrumbs,
  content,
  suppliers,
  sliderCards,
  bodyTop,
  searchData,
  author,
  introductionContent,
  statusCode,
  showSuppliers = true,
  showMarketingSlider = true,
}) => {
  const { language, market } = Runtime.useLocale();
  const { isShowing, onClose, experimentState } = useShowAppBanner(language);
  // The view event sends the state of the moment the banner shows.
  const experimentStateRef = useRef(experimentState);
  experimentStateRef.current = experimentState;
  const { path } = Runtime.usePath();
  const isSwissPage = market === 'CH';

  const {
    hasStageAbove,
    includeTrustSeals,
    isMinimal,
    isModernStage,
    isPlain,
    isPromo,
    isErrorPage,
    showMarketingSlider: hasMarketingSlider,
    showBreadcrumbs,
  } = resolveVariantConfig(variant);

  useEffect(() => {
    if (isShowing) {
      analytics.gtm.trackAppPromoBannerViewed();
      analytics.generic.trackAppPromoBannerViewed(experimentStateRef.current);
    }
  }, [isShowing]);

  const { abTests, isAbTestReady } = useSiteInfo();
  const activeVariant = isAbTestReady
    ? abTests.stage_image_visibility_test
    : '';
  const isStageVisibility = activeVariant === 'better_stage_image_visibility';

  const islandSearchData =
    ISLAND_STAGE_SEARCH_DATA[path.replace(/\.html$/, '')];
  const pageData = islandSearchData ?? searchData;

  const searchProviderKey = islandSearchData ? 'island' : 'stable';

  return (
    <SearchFormLegacy.SearchProvider
      key={searchProviderKey}
      pageData={pageData}
    >
      <PageLegacy.PageContentBase>
        {isPromo && introductionContent}
        {isErrorPage && (
          <ErrorPage.Intro statusCode={statusCode as ErrorPage.ErrorCode} />
        )}
        {bodyTop}
        {!isErrorPage && showSuppliers && (
          <Suppliers.Suppliers
            title={title}
            suppliers={suppliers.content}
            hasStageAbove={hasStageAbove}
            includeTrustSeals={includeTrustSeals}
            isMinimal={isMinimal}
            isModernStage={isModernStage}
            isPlain={isPlain}
            author={author}
            isStageVisibility={isStageVisibility}
          />
        )}
        {hasMarketingSlider && showMarketingSlider && !isSwissPage && (
          <MarketingSlider.MarketingSlider
            sliderCards={sliderCards?.content?.sliderCards}
          />
        )}
        {isErrorPage && <ErrorPage.InfoCards />}
        {breadcrumbs && showBreadcrumbs && (
          <PageNavigationLegacy.Breadcrumbs breadcrumbs={breadcrumbs.links} />
        )}
        <ContentModules content={content} />
        <>
          <div id="fglayer" role="application" />
          <AppInstallBanner isShowing={isShowing} onClose={onClose} />
        </>
      </PageLegacy.PageContentBase>
    </SearchFormLegacy.SearchProvider>
  );
};

export { Body };
export type { BodyProps };
